<template>
  <div class="document-correction">
    <!-- 主页面 - 上传和历史记录 -->
    <div v-if="currentView === 'main'" class="main-view">
      <!-- 上传组件 -->
      <div class="upload-section">
        <el-card>
          <div slot="header">
            <span>文档上传</span>
          </div>
          <el-upload
            ref="upload"
            class="upload-area"
            :auto-upload="false"
            :on-change="handleFileChange"
            :before-remove="beforeRemove"
            :show-file-list="false"
            accept=".doc,.docx"
          >
            <div v-if="!uploading" class="upload-content">
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">
                将文件拖到此处，或<em>点击上传</em>
              </div>
              <div class="el-upload__tip">
                <div>支持格式：.doc, .docx</div>
                <div>文件大小不超过10MB</div>
              </div>
            </div>
            <div v-else class="uploading-content">
              <i class="el-icon-loading"></i>
              <div class="upload-text">{{ uploadText }}</div>
              <el-progress 
                :percentage="uploadProgress" 
                :stroke-width="6"
                status="success"
              ></el-progress>
            </div>
          </el-upload>
        </el-card>
      </div>

      <!-- 历史记录列表 -->
      <div class="history-section">
        <el-card>
          <div slot="header">
            <span>纠错历史</span>
            <el-button 
              style="float: right; padding: 3px 0" 
              type="text"
              @click="refreshHistory"
            >
              刷新
            </el-button>
          </div>
          
          <!-- 有数据时显示表格 -->
          <el-table 
            v-if="historyList.length > 0"
            :data="historyList" 
            style="width: 100%"
            v-loading="historyLoading"
          >
            <el-table-column prop="fileName" label="文档名称" min-width="200">
              <template slot-scope="scope">
                <el-button 
                  type="text" 
                  @click="viewDocument(scope.row)"
                  class="file-name-btn"
                >
                  {{ scope.row.fileName }}
                </el-button>
              </template>
            </el-table-column>
            <el-table-column prop="uploadTime" label="上传时间" width="180">
              <template slot-scope="scope">
                {{ formatTime(scope.row.uploadTime) }}
              </template>
            </el-table-column>
            <el-table-column prop="errorCount" label="纠错数" width="100" align="center">
              <template slot-scope="scope">
                <el-tag 
                  :type="scope.row.errorCount > 0 ? 'danger' : 'success'"
                  size="small"
                >
                  {{ scope.row.errorCount }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="wordCount" label="字数" width="100" align="center">
              <template slot-scope="scope">
                {{ scope.row.wordCount }}
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tag 
                  :type="getStatusType(scope.row.status)"
                  size="small"
                >
                  {{ getStatusText(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="120" align="center">
              <template slot-scope="scope">
                <el-button 
                  size="mini" 
                  type="primary"
                  @click="viewDocument(scope.row)"
                >
                  查看
                </el-button>
                <el-button 
                  size="mini" 
                  type="danger"
                  @click="deleteDocument(scope.row)"
                >
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 无数据时显示空状态 -->
          <div v-else class="empty-state">
            <div class="empty-icon">
              <i class="el-icon-document"></i>
            </div>
            <div class="empty-text">暂无纠错记录</div>
            <div class="empty-description">
              上传您的第一个文档开始使用吧！
            </div>
          </div>
        </el-card>
      </div>
    </div>

    <!-- 编辑页面 - 内容编辑和纠错 -->
    <div v-else-if="currentView === 'edit'" class="edit-view">
      <!-- 顶部工具栏 -->
      <div class="toolbar">
        <el-button 
          type="text" 
          icon="el-icon-arrow-left"
          @click="backToMain"
        >
          返回列表
        </el-button>
        <div class="toolbar-info">
          <span class="file-name">{{ currentDocument.fileName }}</span>
          <el-divider direction="vertical"></el-divider>
          <span class="word-count">字数：{{ currentWordCount }}</span>
        </div>
      </div>

      <div class="edit-content">
        <!-- 左侧编辑区域 70% -->
        <div class="editor-panel">
          <el-card class="editor-card">
            <div slot="header">
              <span>文档内容</span>
              <div style="float: right;">
                <el-button size="mini" @click="saveDocument">保存</el-button>
                <el-button size="mini" type="primary" @click="exportDocument">导出</el-button>
              </div>
            </div>
            <div 
              ref="editor"
              class="rich-editor"
              contenteditable="true"
              @input="handleContentChange"
              v-html="editorContent"
            ></div>
          </el-card>
        </div>

        <!-- 右侧校对区域 30% -->
        <div class="correction-panel">
          <el-card class="correction-card">
            <div slot="header">
              <span>智能校对</span>
            </div>
            
            <!-- 校对按钮 -->
            <div class="correction-actions">
              <el-button 
                type="primary" 
                :loading="correctionLoading"
                @click="startCorrection"
                block
              >
                <i class="el-icon-search"></i>
                {{ correctionLoading ? '校对中...' : '立即校对' }}
              </el-button>
            </div>

            <!-- 校对结果 -->
            <div class="correction-results" v-if="correctionErrors.length > 0">
              <div class="results-header">
                <span>发现 {{ correctionErrors.length }} 处错误</span>
                <el-button 
                  size="mini" 
                  type="text"
                  @click="acceptAllCorrections"
                >
                  全部接受
                </el-button>
              </div>
              
              <div class="error-list">
                <div 
                  v-for="(error, index) in correctionErrors" 
                  :key="index"
                  class="error-item"
                  :class="{ 'error-item-active': selectedErrorIndex === index }"
                  @click="selectError(index)"
                >
                  <div class="error-type">
                    <el-tag 
                      :type="getErrorTypeColor(error.type)" 
                      size="mini"
                    >
                      {{ getErrorTypeText(error.type) }}
                    </el-tag>
                  </div>
                  <div class="error-content">
                    <div class="error-original">
                      原文：<span class="error-text">{{ error.original }}</span>
                    </div>
                    <div class="error-suggestion">
                      建议：<span class="suggestion-text">{{ error.suggestion }}</span>
                    </div>
                    <div class="error-reason" v-if="error.reason">
                      {{ error.reason }}
                    </div>
                  </div>
                  <div class="error-actions">
                    <el-button 
                      size="mini" 
                      type="success"
                      @click.stop="acceptCorrection(index)"
                    >
                      接受
                    </el-button>
                    <el-button 
                      size="mini"
                      @click.stop="ignoreCorrection(index)"
                    >
                      忽略
                    </el-button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 无错误状态 -->
            <div v-else-if="hasChecked && correctionErrors.length === 0" class="no-errors">
              <div class="no-errors-icon">
                <i class="el-icon-circle-check"></i>
              </div>
              <div class="no-errors-text">未发现错误</div>
              <div class="no-errors-desc">您的文档看起来很棒！</div>
            </div>

            <!-- 初始状态 -->
            <div v-else-if="!hasChecked" class="correction-tip">
              <div class="tip-icon">
                <i class="el-icon-info"></i>
              </div>
              <div class="tip-text">
                点击"立即校对"开始检查文档中的错误
              </div>
            </div>
          </el-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 需要安装的依赖包：
// npm install mammoth  // 用于解析Word文档
// npm install jszip    // mammoth的依赖

// 后端接口说明：
// POST /api/correction - 文档校对接口
// 请求参数: { content: string, documentId: string }
// 返回格式: { errors: Array<{id, type, original, suggestion, reason, position}> }
export default {
  name: 'DocumentCorrection',
  data() {
    return {
      currentView: 'main', // 'main' | 'edit'
      // 移除上传接口，前端直接解析
      uploading: false,
      uploadProgress: 0,
      uploadText: '正在上传文档...',
      
      // 历史记录
      historyList: [
        // 示例数据，实际从后端获取
        {
          id: 1,
          fileName: '年度报告.docx',
          uploadTime: new Date(),
          errorCount: 5,
          wordCount: 2580,
          status: 'completed'
        },
        {
          id: 2,
          fileName: '项目计划书.doc',
          uploadTime: new Date(Date.now() - 86400000),
          errorCount: 0,
          wordCount: 1920,
          status: 'completed'
        }
      ],
      historyLoading: false,

      // 当前编辑文档
      currentDocument: {},
      editorContent: '',
      currentWordCount: 0,

      // 校对相关
      correctionLoading: false,
      correctionErrors: [],
      hasChecked: false,
      selectedErrorIndex: -1
    }
  },

  methods: {
    // 文件选择处理
    async handleFileChange(file) {
      const isValidFormat = file.name.toLowerCase().endsWith('.doc') || 
                          file.name.toLowerCase().endsWith('.docx');
      const isLt10M = file.size / 1024 / 1024 < 10;

      if (!isValidFormat) {
        this.$message.error('只支持 .doc 和 .docx 格式的文件！');
        return;
      }

      if (!isLt10M) {
        this.$message.error('文件大小不能超过 10MB！');
        return;
      }

      // 开始解析文档
      this.uploading = true;
      this.uploadText = '正在解析文档...';
      this.uploadProgress = 30;

      try {
        const content = await this.readWordFile(file.raw);
        
        if (!content || content.trim().length === 0) {
          this.$message.error('文档内容为空或无法读取，请检查文档是否损坏！');
          return;
        }

        this.uploadProgress = 80;
        this.uploadText = '解析完成，正在跳转...';

        // 创建新文档记录
        const newDoc = {
          id: Date.now(),
          fileName: file.name,
          content: content,
          wordCount: this.countWords(content),
          uploadTime: new Date(),
          errorCount: 0,
          status: 'pending'
        };

        // 延迟一下让用户看到完成状态
        setTimeout(() => {
          this.currentDocument = newDoc;
          this.editorContent = content.replace(/\n/g, '<br>');
          this.currentWordCount = newDoc.wordCount;
          this.currentView = 'edit';
          this.uploading = false;
          this.uploadProgress = 0;
          this.$message.success('文档解析成功！');
        }, 500);

      } catch (error) {
        this.$message.error('文档解析失败，请确保文档格式正确且未损坏！');
        this.uploading = false;
        this.uploadProgress = 0;
      }
    },

    // 防止文件被移除
    beforeRemove() {
      return false;
    },

    // 读取Word文档内容
    async readWordFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            // 使用mammoth解析Word文档
            const mammoth = require('mammoth');
            const arrayBuffer = e.target.result;
            const result = await mammoth.extractRawText({arrayBuffer});
            resolve(result.value);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    },



    // 统计字数
    countWords(text) {
      return text.replace(/\s/g, '').length;
    },

    // 格式化时间
    formatTime(time) {
      return new Date(time).toLocaleString('zh-CN');
    },

    // 获取状态样式
    getStatusType(status) {
      const types = {
        'pending': 'warning',
        'completed': 'success',
        'error': 'danger'
      };
      return types[status] || 'info';
    },

    // 获取状态文字
    getStatusText(status) {
      const texts = {
        'pending': '待处理',
        'completed': '已完成',
        'error': '出错'
      };
      return texts[status] || '未知';
    },

    // 刷新历史记录
    async refreshHistory() {
      this.historyLoading = true;
      try {
        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 1000));
        // 实际应该调用API获取数据
        // const response = await this.$http.get('/api/correction-history');
        // this.historyList = response.data;
      } finally {
        this.historyLoading = false;
      }
    },

    // 查看文档
    viewDocument(doc) {
      this.currentDocument = doc;
      this.editorContent = doc.content || '文档内容加载中...';
      this.currentWordCount = doc.wordCount || 0;
      this.currentView = 'edit';
      
      // 如果有纠错记录，加载纠错结果
      if (doc.errorCount > 0) {
        this.loadCorrectionResults(doc.id);
      }
    },

    // 删除文档
    deleteDocument(doc) {
      this.$confirm('确定要删除这个文档吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const index = this.historyList.findIndex(item => item.id === doc.id);
        if (index > -1) {
          this.historyList.splice(index, 1);
          this.$message.success('删除成功！');
        }
      });
    },

    // 返回主页
    backToMain() {
      this.currentView = 'main';
      this.correctionErrors = [];
      this.hasChecked = false;
      this.selectedErrorIndex = -1;
    },

    // 内容变化
    handleContentChange() {
      const content = this.$refs.editor.innerText || '';
      this.currentWordCount = this.countWords(content);
    },

    // 保存文档
    saveDocument() {
      //const content = this.$refs.editor.innerHTML;
      // 调用保存API
      this.$message.success('保存成功！');
    },

    // 导出文档
    exportDocument() {
      // 实现导出功能
      this.$message.success('导出成功！');
    },

    // 开始校对
    async startCorrection() {
      this.correctionLoading = true;
      this.hasChecked = false;
      
      try {
        const content = this.$refs.editor.innerText || '';
        
        if (!content.trim()) {
          this.$message.warning('请先输入要校对的内容');
          return;
        }

        // 调用后端校对接口
        const response = await this.$http.post('/api/correction', {
          content: content,
          documentId: this.currentDocument.id
        });
        
        this.correctionErrors = response.data.errors || [];
        this.hasChecked = true;
        this.highlightErrors();
        
        // 更新文档错误统计
        this.currentDocument.errorCount = this.correctionErrors.length;
        this.currentDocument.correctionTime = new Date();
        this.currentDocument.status = 'completed';
        
        // 更新历史记录
        const existingIndex = this.historyList.findIndex(item => item.id === this.currentDocument.id);
        if (existingIndex > -1) {
          this.historyList[existingIndex] = { ...this.currentDocument };
        } else {
          this.historyList.unshift({ ...this.currentDocument });
        }
        
        if (this.correctionErrors.length > 0) {
          this.$message.success(`校对完成，发现 ${this.correctionErrors.length} 处错误`);
        } else {
          this.$message.success('校对完成，未发现错误');
        }
      } catch (error) {
        this.$message.error('校对失败，请重试');
        console.error('校对接口错误:', error);
      } finally {
        this.correctionLoading = false;
      }
    },

    // 高亮错误
    highlightErrors() {
      const editor = this.$refs.editor;
      let content = editor.innerHTML;
      
      // 为每个错误添加高亮样式
      this.correctionErrors.forEach((error, index) => {
        const highlightClass = `error-highlight error-${error.type}`;
        content = content.replace(
          error.original,
          `<span class="${highlightClass}" data-error-index="${index}">${error.original}</span>`
        );
      });
      
      editor.innerHTML = content;
    },

    // 选择错误
    selectError(index) {
      this.selectedErrorIndex = index;
      
      // 滚动到对应错误位置
      const errorSpan = document.querySelector(`[data-error-index="${index}"]`);
      if (errorSpan) {
        errorSpan.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorSpan.classList.add('error-selected');
        
        // 移除其他选中状态
        document.querySelectorAll('.error-selected').forEach(el => {
          if (el !== errorSpan) {
            el.classList.remove('error-selected');
          }
        });
      }
    },

    // 接受修正
    acceptCorrection(index) {
      const error = this.correctionErrors[index];
      const editor = this.$refs.editor;
      let content = editor.innerHTML;
      
      content = content.replace(
        `<span class="error-highlight error-${error.type}" data-error-index="${index}">${error.original}</span>`,
        error.suggestion
      );
      
      editor.innerHTML = content;
      this.correctionErrors.splice(index, 1);
      this.handleContentChange();
      
      this.$message.success('修正已应用');
    },

    // 忽略错误
    ignoreCorrection(index) {
      const error = this.correctionErrors[index];
      const errorSpan = document.querySelector(`[data-error-index="${index}"]`);
      if (errorSpan) {
        errorSpan.outerHTML = error.original;
      }
      
      this.correctionErrors.splice(index, 1);
      this.$message.info('已忽略该建议');
    },

    // 接受所有修正
    acceptAllCorrections() {
      this.$confirm('确定要接受所有修正建议吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(() => {
        // 从后往前处理，避免索引问题
        for (let i = this.correctionErrors.length - 1; i >= 0; i--) {
          this.acceptCorrection(i);
        }
        this.$message.success('所有修正已应用');
      });
    },

    // 获取错误类型颜色
    getErrorTypeColor(type) {
      const colors = {
        'grammar': 'danger',
        'spelling': 'warning',
        'punctuation': 'info',
        'style': 'success'
      };
      return colors[type] || 'info';
    },

    // 获取错误类型文字
    getErrorTypeText(type) {
      const texts = {
        'grammar': '语法',
        'spelling': '拼写',
        'punctuation': '标点',
        'style': '样式'
      };
      return texts[type] || '其他';
    },

    // 加载纠错结果
    //async loadCorrectionResults(docId) {
    async loadCorrectionResults() {
      // 从后端加载纠错结果
      // const response = await this.$http.get(`/api/correction-results/${docId}`);
      // this.correctionErrors = response.data;
    }
  },

  mounted() {
    // 页面加载时获取历史记录
    this.refreshHistory();
  }
}
</script>

<style scoped>
.document-correction {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: calc(100vh - 60px);
}

/* 主页面样式 */
.main-view {
  max-width: 1200px;
  margin: 0 auto;
}

.upload-section {
  margin-bottom: 20px;
}

.upload-area {
  width: 100%;
}

.upload-area .el-upload {
  width: 100%;
}

.upload-area .el-upload-dragger {
  width: 100%;
  height: 180px;
}

.upload-content {
  text-align: center;
  padding: 20px;
}

.upload-content i {
  font-size: 48px;
  color: #409eff;
  margin-bottom: 16px;
}

.uploading-content {
  text-align: center;
  padding: 40px;
}

.uploading-content i {
  font-size: 48px;
  color: #409eff;
  margin-bottom: 16px;
}

.upload-text {
  margin: 16px 0;
  font-size: 14px;
  color: #666;
}

.el-upload__tip {
  margin-top: 12px;
  color: #999;
  font-size: 12px;
}

/* 空状态样式 */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-icon i {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 18px;
  margin-bottom: 8px;
}

.empty-description {
  font-size: 14px;
}

/* 文件名按钮样式 */
.file-name-btn {
  text-align: left;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 编辑页面样式 */
.edit-view {
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
}

.toolbar {
  display: flex;
  align-items: center;
  background: white;
  padding: 12px 20px;
  border-radius: 4px;
  margin-bottom: 16px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.toolbar-info {
  margin-left: 20px;
  display: flex;
  align-items: center;
}

.file-name {
  font-weight: bold;
  font-size: 16px;
}

.word-count {
  color: #666;
  font-size: 14px;
}

.edit-content {
  display: flex;
  gap: 16px;
  flex: 1;
  overflow: hidden;
}

.editor-panel {
  flex: 7;              /* 占 70% 宽度 */
  display: flex;
  flex-direction: column;
}

.correction-panel {
  flex: 3;              /* 占 30% 宽度 */
  display: flex;
  flex-direction: column;
}

.editor-card,
.correction-card {
  flex: 1;              /* 高度自适应 */
  display: flex;
  flex-direction: column;
}

.editor-card .el-card__body,
.correction-card .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

/* 富文本编辑器样式 */
.rich-editor {
  flex: 1;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 15px;
  font-size: 14px;
  line-height: 1.6;
  overflow-y: auto;
  background: white;
  outline: none;
}

.rich-editor:focus {
  border-color: #409eff;
}

/* 错误高亮样式 */
.error-highlight {
  position: relative;
  cursor: pointer;
}

.error-grammar {
  border-bottom: 2px wavy #f56c6c;
}

.error-spelling {
  border-bottom: 2px wavy #e6a23c;
}

.error-punctuation {
  border-bottom: 2px wavy #409eff;
}

.error-style {
  border-bottom: 2px wavy #67c23a;
}

.error-selected {
  background-color: rgba(64, 158, 255, 0.1);
  border-radius: 2px;
}

/* 校对区域样式 */
.correction-actions {
  margin-bottom: 20px;
}

.correction-actions .el-button {
  width: 100%;
  height: 40px;
  font-size: 14px;
}

.correction-results {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.error-list {
  flex: 1;
  overflow-y: auto;
}

.error-item {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  margin-bottom: 12px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.error-item:hover,
.error-item-active {
  border-color: #409eff;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2);
}

.error-type {
  margin-bottom: 8px;
}

.error-content {
  margin-bottom: 10px;
}

.error-original {
  margin-bottom: 4px;
  font-size: 13px;
}

.error-text {
  background-color: #fef0f0;
  color: #f56c6c;
  padding: 2px 4px;
  border-radius: 2px;
  font-weight: bold;
}

.error-suggestion {
  margin-bottom: 6px;
  font-size: 13px;
}

.suggestion-text {
  background-color: #f0f9ff;
  color: #409eff;
  padding: 2px 4px;
  border-radius: 2px;
  font-weight: bold;
}

.error-reason {
  font-size: 12px;
  color: #909399;
  font-style: italic;
}

.error-actions {
  display: flex;
  gap: 8px;
}

.error-actions .el-button {
  padding: 4px 8px;
  font-size: 12px;
}

/* 无错误状态 */
.no-errors {
  text-align: center;
  padding: 40px 20px;
  color: #67c23a;
}

.no-errors-icon i {
  font-size: 48px;
  margin-bottom: 12px;
}

.no-errors-text {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
}

.no-errors-desc {
  font-size: 14px;
  color: #909399;
}

/* 初始提示状态 */
.correction-tip {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.tip-icon i {
  font-size: 48px;
  margin-bottom: 12px;
}

.tip-text {
  font-size: 14px;
  line-height: 1.5;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .edit-content {
    flex-direction: column;
  }
  
  .editor-panel,
  .correction-panel {
    width: 100%;
  }
  
  .correction-panel {
    max-height: 400px;
  }
}

/* 滚动条样式 */
.error-list::-webkit-scrollbar,
.rich-editor::-webkit-scrollbar {
  width: 6px;
}

.error-list::-webkit-scrollbar-track,
.rich-editor::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.error-list::-webkit-scrollbar-thumb,
.rich-editor::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.error-list::-webkit-scrollbar-thumb:hover,
.rich-editor::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 表格样式优化 */
.el-table .el-table__cell {
  padding: 12px 0;
}

/* 卡片样式优化 */
.el-card {
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
}

/* 进度条样式 */
.el-progress {
  margin-top: 10px;
}

/* 按钮样式优化 */
.el-button + .el-button {
  margin-left: 8px;
}

/* 标签样式 */
.el-tag {
  border-radius: 12px;
}

/* 上传区域样式优化 */
.el-upload-dragger {
  border: 2px dashed #d9d9d9;
  border-radius: 6px;
  transition: border-color 0.3s;
}

.el-upload-dragger:hover {
  border-color: #409eff;
}

/* 工具提示样式 */
.el-upload__tip div {
  line-height: 1.5;
}

/* 加载状态样式 */
.uploading-content .el-progress {
  width: 200px;
  margin: 0 auto;
}

/* 文件列表样式 */
.el-table__row:hover > td {
  background-color: #f5f7fa;
}

/* 分割线样式 */
.el-divider--vertical {
  height: 1.2em;
}
</style>